import { Side, TileSideIOType } from '@/schemas/tiles';
import { Rotation, rotateDirection } from '@/models/data/Rotation';
import { TileInstance } from '@/models/tiles/TileInstance';
import { TileType } from '@/models/TileTypes';
import { TileDefinition, getEffectiveSideIO } from '@/models/tiles/TileDefinition';
import { Direction, directionToPoint, opposite } from '@/utils/direction';
import { Point } from '@/models/Point';
import type { TileId } from '@/types/ids';
import type { ITileManagerModel } from '@/interfaces/models';
import { ConnectionGraph } from '@/models/connections/ConnectionGraph';

const directionalSides: Side[] = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT];

const sideBaseDirection: Record<Side, Direction | null> = {
  [Side.TOP]: Direction.UP,
  [Side.RIGHT]: Direction.RIGHT,
  [Side.BOTTOM]: Direction.DOWN,
  [Side.LEFT]: Direction.LEFT,
};

export interface ConnectionCalculatorDeps {
  getTileDefinition: (type: TileType) => TileDefinition<unknown>;
}

export interface ConnectionTileIndex {
  readonly byId: ReadonlyMap<TileId, TileInstance>;
  readonly byPosition: ReadonlyMap<string, TileInstance>;
}

const orderedSelections = new WeakMap<
  ConnectionTileIndex,
  (
    affected: ReadonlySet<TileId>,
    tiles: readonly TileInstance[],
  ) => TileInstance[] | undefined
>();

/** Internal tracker registration; unknown index objects retain the array walk. */
export function registerConnectionTileOrder(
  index: ConnectionTileIndex,
  select: (
    affected: ReadonlySet<TileId>,
    tiles: readonly TileInstance[],
  ) => TileInstance[] | undefined,
): () => void {
  orderedSelections.set(index, select);
  return () => orderedSelections.delete(index);
}

export function connectionLocationKey(
  tile: TileInstance,
  point?: Point | { x: number; y: number },
): string {
  const value = point ?? tile.location;
  const locationPoint = value instanceof Point ? value : Point.fromData(value);
  return `${tile.globalTilePosition.chipInstanceId}:${locationPoint.x},${locationPoint.y}`;
}

/**
 * ConnectionCalculator is responsible for calculating which tiles should be connected
 * based on their positions, rotations, and IO configurations.
 */
export class ConnectionCalculator {
  constructor(private readonly deps: ConnectionCalculatorDeps) {}

  /**
   * Calculate all connections for tiles in a tile manager
   */
  calculateConnections(
    tileManager: ITileManagerModel,
    connectionGraph: ConnectionGraph,
  ): void {
    connectionGraph.clear();

    const tiles = tileManager.tiles;
    const tileIndex = this.buildTileIndex(tiles);

    for (const tile of tiles) {
      this.calculateConnectionsForTile(tile, tileIndex, connectionGraph);
    }
  }

  /**
   * Recalculate connections for a specific tile and its neighbors
   */
  recalculateConnectionsForTile(
    tileId: TileId,
    tileManager: ITileManagerModel,
    connectionGraph: ConnectionGraph,
  ): Set<TileId> {
    return this.recalculateConnectionsForTiles([tileId], tileManager, connectionGraph);
  }

  /** Calculate one edit transaction without rebuilding the index for every tile. */
  recalculateConnectionsForTiles(
    tileIds: Iterable<TileId>,
    tileManager: ITileManagerModel,
    connectionGraph: ConnectionGraph,
    index?: ConnectionTileIndex,
  ): Set<TileId> {
    const affected = new Set(tileIds);
    const tiles = tileManager.tiles;
    const tileIndex = index?.byPosition ?? this.buildTileIndex(tiles);
    const toCalculate = new Set<TileInstance>();
    const changedTiles =
      (index && orderedSelections.get(index)?.(affected, tiles)) ??
      tiles.filter((tile) => affected.has(tile.id));
    for (const tile of changedTiles) {
      toCalculate.add(tile);
      for (const neighbor of this.getNeighborTiles(tile, tileIndex))
        toCalculate.add(neighbor);
    }
    for (const tile of toCalculate) affected.add(tile.id);
    // Manual links may cross chip-instance scopes. Their other endpoints need
    // flag publication even when they are outside the geometric rebuild set.
    const removedEndpoints = new Set<TileId>();
    for (const tileId of affected) {
      for (const edge of connectionGraph.getConnectionsForTile(tileId)) {
        removedEndpoints.add(edge.a.tileId);
        removedEndpoints.add(edge.b.tileId);
      }
    }
    connectionGraph.removeConnectionsForTiles(affected);
    for (const tile of toCalculate)
      this.calculateConnectionsForTile(tile, tileIndex, connectionGraph);
    for (const tileId of removedEndpoints) affected.add(tileId);
    return affected;
  }

  /**
   * Clear all connections for a tile
   */
  clearConnectionsForTile(tileId: TileId, connectionGraph: ConnectionGraph): void {
    connectionGraph.removeConnectionsForTile(tileId);
  }

  private calculateConnectionsForTile(
    tile: TileInstance,
    tileIndex: ReadonlyMap<string, TileInstance>,
    connectionGraph: ConnectionGraph,
  ): void {
    for (const side of directionalSides) {
      const neighborLocation = this.getNeighborLocation(tile, side);
      if (!neighborLocation) {
        continue;
      }

      const neighborTile = tileIndex.get(neighborLocation.key);
      if (!neighborTile) {
        continue;
      }

      const neighborSide = this.findFacingSide(
        neighborTile,
        neighborLocation.oppositeDirection,
      );
      if (!neighborSide) {
        continue;
      }

      if (!this.canConnect(tile, side, neighborTile, neighborSide)) {
        continue;
      }

      // Add connection (ConnectionGraph will handle deduplication)
      connectionGraph.addConnection(
        { tileId: tile.id, side },
        { tileId: neighborTile.id, side: neighborSide },
      );
    }
  }

  canConnect(
    tile: TileInstance,
    side: Side,
    neighbor: TileInstance,
    neighborSide: Side,
    options: { allowOptional?: boolean } = {},
  ): boolean {
    const { allowOptional = false } = options;

    const tileDefinition = this.deps.getTileDefinition(tile.tileType);
    const neighborDefinition = this.deps.getTileDefinition(neighbor.tileType);

    const sideInfo = getEffectiveSideIO(tile, side, tileDefinition);
    const neighborSideInfo = getEffectiveSideIO(
      neighbor,
      neighborSide,
      neighborDefinition,
    );

    if (!sideInfo || !neighborSideInfo) {
      return false;
    }

    // Never connect to sides that don't accept connections (NONE)
    if (
      sideInfo.type === TileSideIOType.NONE ||
      neighborSideInfo.type === TileSideIOType.NONE
    ) {
      return false;
    }

    if (!allowOptional) {
      // Optional connections require at least one side to opt-in via forceConnection
      if (!sideInfo.forceConnection && !neighborSideInfo.forceConnection) {
        return false;
      }
    }

    const sideCanOutput =
      sideInfo.type === TileSideIOType.OUTPUT ||
      sideInfo.type === TileSideIOType.INPUT_OUTPUT;
    const sideCanInput =
      sideInfo.type === TileSideIOType.INPUT ||
      sideInfo.type === TileSideIOType.INPUT_OUTPUT;

    const neighborCanOutput =
      neighborSideInfo.type === TileSideIOType.OUTPUT ||
      neighborSideInfo.type === TileSideIOType.INPUT_OUTPUT;
    const neighborCanInput =
      neighborSideInfo.type === TileSideIOType.INPUT ||
      neighborSideInfo.type === TileSideIOType.INPUT_OUTPUT;

    return (sideCanOutput && neighborCanInput) || (neighborCanOutput && sideCanInput);
  }

  isConnectionForced(
    tile: TileInstance,
    side: Side,
    neighbor: TileInstance,
    neighborSide: Side,
  ): boolean {
    const tileDefinition = this.deps.getTileDefinition(tile.tileType);
    const neighborDefinition = this.deps.getTileDefinition(neighbor.tileType);

    const sideInfo = getEffectiveSideIO(tile, side, tileDefinition);
    const neighborSideInfo = getEffectiveSideIO(
      neighbor,
      neighborSide,
      neighborDefinition,
    );

    return Boolean(sideInfo?.forceConnection || neighborSideInfo?.forceConnection);
  }

  private findFacingSide(tile: TileInstance, targetDirection: Direction): Side | null {
    for (const side of directionalSides) {
      const worldDirection = this.getWorldDirection(tile, side);
      if (worldDirection !== null && worldDirection === targetDirection) {
        return side;
      }
    }

    return null;
  }

  private getNeighborLocation(tile: TileInstance, side: Side) {
    const worldDirection = this.getWorldDirection(tile, side);
    if (worldDirection === null) {
      return null;
    }

    const offset = directionToPoint(worldDirection);
    const currentLocation = this.asPoint(tile.location);
    const neighborPoint = currentLocation.add(offset);
    const key = this.locationKey(tile, neighborPoint);
    const oppositeDirection = opposite(worldDirection);

    return { key, oppositeDirection };
  }

  private getWorldDirection(tile: TileInstance, side: Side): Direction | null {
    const baseDirection = sideBaseDirection[side];
    if (baseDirection === null) {
      return null;
    }

    return rotateDirection(
      baseDirection,
      (tile.meta.rotation as Rotation) ?? Rotation.CW_0,
    );
  }

  private getNeighborTiles(
    tile: TileInstance,
    tileIndex: ReadonlyMap<string, TileInstance>,
  ): TileInstance[] {
    const neighbors: TileInstance[] = [];
    for (const side of directionalSides) {
      const neighborLocation = this.getNeighborLocation(tile, side);
      if (!neighborLocation) {
        continue;
      }

      const neighbor = tileIndex.get(neighborLocation.key);
      if (neighbor) {
        neighbors.push(neighbor);
      }
    }

    return neighbors;
  }

  private buildTileIndex(tiles: TileInstance[]): Map<string, TileInstance> {
    const index = new Map<string, TileInstance>();
    for (const tile of tiles) {
      index.set(this.locationKey(tile), tile);
    }
    return index;
  }

  private locationKey(
    tile: TileInstance,
    point?: Point | { x: number; y: number },
  ): string {
    return connectionLocationKey(tile, point);
  }

  private asPoint(value: Point | { x: number; y: number }): Point {
    return value instanceof Point ? value : Point.fromData(value);
  }
}
